import React, { useState, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tabs } from "@/components/ui/tabs";
import { TimePeriod, generateMonthlyActivityData, getTimeRange } from "@/utils/dashboardUtils";
import { BikeRide } from "@/api/users/types";
import { userStatsService } from "@/api/users/userStatsService";
import StatisticsHeader from "./components/StatisticsHeader";
import StatisticsTabs from "./components/StatisticsTabs";
import OverviewTab from "./components/tabs/OverviewTab";
import BikesTab from "./components/tabs/BikesTab";
import UsersTab from "./components/tabs/UsersTab";
import RidesTab from "./components/tabs/RidesTab";
import { exportStatisticsData } from "./utils/exportUtils";
import { toast } from "sonner";
import DashboardFiltersComponent, { DashboardFilters } from "../dashboard/components/DashboardFilters";
import TimeRangeSelector from "./components/TimeRangeSelector";

const StatisticsPage: React.FC = () => {
  const [activePeriod, setActivePeriod] = useState<TimePeriod>("month");
  const [filters, setFilters] = useState<DashboardFilters>({
    regionId: "all",
    organizationId: "all",
    siteId: "all"
  });

  // Helper function to determine site IDs for filtering
  const getSiteIdsForFilters = async (filters: DashboardFilters): Promise<string[] | null> => {
    if (filters.siteId && filters.siteId !== 'all') {
      return [filters.siteId];
    } else if (filters.organizationId && filters.organizationId !== 'all') {
      const { data: orgSites } = await supabase
        .from('sites')
        .select('id')
        .eq('organization_id', filters.organizationId);
      
      return orgSites && orgSites.length > 0 ? orgSites.map(site => site.id) : null;
    } else if (filters.regionId && filters.regionId !== 'all') {
      const { data: regionOrgs } = await supabase
        .from('organizations')
        .select('id')
        .eq('region_id', filters.regionId);
      
      if (regionOrgs && regionOrgs.length > 0) {
        const orgIds = regionOrgs.map(org => org.id);
        const { data: regionSites } = await supabase
          .from('sites')
          .select('id')
          .in('organization_id', orgIds);
        
        return regionSites && regionSites.length > 0 ? regionSites.map(site => site.id) : null;
      }
    }
    return null;
  };
  
  // Fetch bikes data
  const bikeQuery = useQuery({
    queryKey: ["bikes", filters],
    queryFn: async () => {
      console.log("Fetching bike data from Supabase with filters", filters);
      
      const siteIdsToFilter = await getSiteIdsForFilters(filters);
      
      let query = supabase
        .from('bikes')
        .select(`
          *,
          model:bike_models(name),
          site:sites(name),
          users:user_location_id(username)
        `)
        .order('created_at', { ascending: false });
      
      if (siteIdsToFilter && siteIdsToFilter.length > 0) {
        query = query.in('site_id', siteIdsToFilter);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error("Error fetching bikes:", error);
        throw error;
      }
      
      console.log("Bike data:", data);
      return data || [];
    },
    refetchOnWindowFocus: false
  });
  
  // Fetch user statistics using ride history - updated to use our service
  const userStatsQuery = useQuery({
    queryKey: ["userStats", activePeriod, filters],
    queryFn: async () => {
      console.log("Fetching user statistics");
      
      // Use our updated service to get user statistics
      const userData = await userStatsService.getUserStatistics();
      
      // Generate monthly activity data - using our new utility function
      const monthlyData = await generateMonthlyActivityData(activePeriod);
      
      // Fixed query for CO2 savings by ride type, properly joining rides and bookings
      const siteIdsToFilter = await getSiteIdsForFilters(filters);
      const timeRange = getTimeRange(activePeriod);
      
      let ridesQuery = supabase
        .from('rides')
        .select(`
          distance_traveled_km,
          count_towards_offset,
          booking_id,
          user_id,
          created_at,
          actual_start_time,
          users (
            username
          ),
          bookings!inner(
            ride_type,
            start_location_id,
            end_location_id,
            bike_id,
            bikes!inner(
              site_id
            )
          )
        `);
      
      // Apply time period filtering
      if (activePeriod !== 'all') {
        ridesQuery = ridesQuery
          .gte('created_at', timeRange.start.toISOString())
          .lte('created_at', timeRange.end.toISOString());
      }
      
      // Apply site filtering if needed
      if (siteIdsToFilter && siteIdsToFilter.length > 0) {
        ridesQuery = ridesQuery.in('bookings.bikes.site_id', siteIdsToFilter);
      }
      
      const { data: ridesAndBookingsData, error: ridesError } = await ridesQuery;
        
      if (ridesError) {
        console.error("Error fetching rides and bookings data:", ridesError);
        // Continue with empty data rather than throwing an error
        return {
          userData,
          userGrowthData: await generateUserGrowthData(),
          monthlyData,
          co2ByTypeData: []
        };
      }
      
      // Calculate CO2 savings by ride type with the fixed join query
      const co2ByTypeData = calculateCO2ByRideType(ridesAndBookingsData || []);
      
      // Generate data for user growth chart
      const userGrowthData = await generateUserGrowthData();

      // Calculate overview statistics
      const overviewStats = {
        totalRides: ridesAndBookingsData?.length || 0,
        totalUsers: userData.totalUsers || 0,
        totalOffsetRides: ridesAndBookingsData?.filter(ride => ride.count_towards_offset !== false).length || 0,
        totalDistance: ridesAndBookingsData?.reduce((acc, ride) => acc + (ride.distance_traveled_km || 0), 0) || 0,
        totalOffsetDistance: ridesAndBookingsData?.reduce((acc, ride) => {
          if (ride.count_towards_offset !== false) {
            return acc + (ride.distance_traveled_km || 0);
          }
          return acc;
        }, 0) || 0,
        totalCO2Offset: ridesAndBookingsData?.reduce((acc, ride) => {
          if (ride.count_towards_offset !== false) {
            return acc + ((ride.distance_traveled_km || 0) * 0.16272); // UK DEFRA conversion factor
          }
          return acc;
        }, 0) || 0
      };

      // Calculate user-specific statistics
      const userRides = new Map();
      ridesAndBookingsData?.forEach(ride => {
        const userId = ride.user_id;
        if (!userRides.has(userId)) {
          userRides.set(userId, {
            totalRides: 0,
            totalDistance: 0,
            offsetDistance: 0,
            nonOffsetDistance: 0,
            username: ride.users?.username || 'Unknown User'
          });
        }
        
        const stats = userRides.get(userId);
        stats.totalRides++;
        const distance = ride.distance_traveled_km || 0;
        stats.totalDistance += distance;
        
        if (ride.count_towards_offset !== false) {
          stats.offsetDistance += distance;
        } else {
          stats.nonOffsetDistance += distance;
        }
      });

      // Find most active user
      let mostActiveUser = null;
      let maxDistance = 0;
      userRides.forEach((stats, userId) => {
        if (stats.totalDistance > maxDistance) {
          maxDistance = stats.totalDistance;
          mostActiveUser = {
            name: stats.username,
            totalRides: stats.totalRides,
            totalDistance: stats.totalDistance
          };
        }
      });

      // Calculate averages
      const totalUsers = userData.totalUsers || 1; // Prevent division by zero
      const averagePerUser = {
        rides: (ridesAndBookingsData?.length || 0) / totalUsers,
        totalDistance: (overviewStats.totalDistance) / totalUsers,
        offsetDistance: (overviewStats.totalOffsetDistance) / totalUsers,
        nonOffsetDistance: (overviewStats.totalDistance - overviewStats.totalOffsetDistance) / totalUsers,
        co2Offset: (overviewStats.totalCO2Offset) / totalUsers
      };

      // Update userData with new statistics
      const updatedUserData = {
        ...userData,
        mostActiveUser,
        averagePerUser
      };
      
      return {
        userData: updatedUserData,
        userGrowthData,
        monthlyData,
        co2ByTypeData,
        overviewStats
      };
    },
    refetchOnWindowFocus: false
  });
  
  // Calculate CO2 savings by ride type with fixed query results
  const calculateCO2ByRideType = (ridesData: any[]): any[] => {
    // CO2 factor: kg CO2 saved per km (compared to car transportation)
    const co2Factor = 0.16272;
    
    // Group by ride type and calculate total distance for eligible rides
    const co2ByType = new Map();
    
    ridesData.forEach(ride => {
      // Safely access ride type from the joined bookings data
      const rideType = ride.bookings?.ride_type || 'unknown';
      
      // Only count rides marked for offset
      if (ride.count_towards_offset !== false) {
        const distance = ride.distance_traveled_km || 0;
        const co2Saved = distance * co2Factor;
        
        if (!co2ByType.has(rideType)) {
          co2ByType.set(rideType, {
            rideType: capitalizeFirstLetter(rideType),
            totalDistance: 0,
            totalCO2: 0
          });
        }
        
        const typeData = co2ByType.get(rideType);
        typeData.totalDistance += distance;
        typeData.totalCO2 += co2Saved;
        co2ByType.set(rideType, typeData);
      }
    });
    
    // Convert to array and round values
    return Array.from(co2ByType.values()).map(item => ({
      rideType: item.rideType,
      totalDistance: Math.round(item.totalDistance * 100) / 100,
      totalCO2: Math.round(item.totalCO2 * 100) / 100
    }));
  };

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };
  
  // Generate user growth data - improved version
  const generateUserGrowthData = async (): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('created_at')
        .order('created_at', { ascending: true });
        
      if (error) {
        console.error("Error fetching user growth data:", error);
        return [];
      }
      
      if (!data || data.length === 0) {
        return [];
      }
      
      // Get the month range for the chart (last 6 months)
      const months = [];
      const currentDate = new Date();
      
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date();
        monthDate.setMonth(currentDate.getMonth() - i);
        
        // Format as MMM YYYY (e.g., Jan 2025)
        const monthKey = monthDate.toLocaleString('default', { 
          month: 'short', 
          year: 'numeric' 
        });
        
        months.push({
          date: new Date(monthDate),
          key: monthKey,
          month: monthKey,
          users: 0
        });
      }
      
      // Count cumulative users by month
      let cumulativeCount = 0;
      
      // Process and populate the months array with user counts
      months.forEach((monthData, index) => {
        const monthStart = new Date(monthData.date);
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);
        
        const monthEnd = new Date(monthData.date);
        if (index === months.length - 1) {
          // If it's the current month, use current date as end
          monthEnd.setHours(23, 59, 59, 999);
        } else {
          // Otherwise, use last day of month
          monthEnd.setMonth(monthEnd.getMonth() + 1, 0);
          monthEnd.setHours(23, 59, 59, 999);
        }
        
        // Count users created in this month
        const monthUsers = data.filter(user => {
          const createdAt = new Date(user.created_at);
          return createdAt <= monthEnd;
        }).length;
        
        // Update the month data
        monthData.users = monthUsers;
      });
      
      return months;
    } catch (error) {
      console.error("Error generating user growth data:", error);
      return [];
    }
  };
  
  // Fetch recent rides - Updated to include bike model ID and serial number
  const recentRidesQuery = useQuery({
    queryKey: ["recentRides", filters, activePeriod],
    queryFn: async () => {
      console.log("Fetching all recent rides from Supabase with filters", filters, "period", activePeriod);
      
      const siteIdsToFilter = await getSiteIdsForFilters(filters);
      const timeRange = getTimeRange(activePeriod);
      
      let ridesQuery = supabase
        .from('rides')
        .select(`
          id,
          user_id,
          booking_id,
          actual_start_time,
          actual_end_time,
          distance_traveled_km,
          created_at,
          count_towards_offset,
          users (username),
          bookings!inner (
            bike_id,
            start_location_type,
            start_location_id,
            end_location_type,
            end_location_id,
            start_site:sites!bookings_start_location_id_fkey(name),
            end_site:sites!bookings_end_location_id_fkey(name),
            bikes!inner (
              id,
              model_id,
              serial_number,
              site_id,
              model:bike_models(name)
            )
          )
        `)
        .order('created_at', { ascending: false });
      
      // Apply time period filtering
      if (activePeriod !== 'all') {
        ridesQuery = ridesQuery
          .gte('created_at', timeRange.start.toISOString())
          .lte('created_at', timeRange.end.toISOString());
      }
      
      // Apply site filtering if needed
      if (siteIdsToFilter && siteIdsToFilter.length > 0) {
        ridesQuery = ridesQuery.in('bookings.bikes.site_id', siteIdsToFilter);
      }
      
      // Apply limit after filtering
      ridesQuery = ridesQuery.limit(50);
      
      const { data, error } = await ridesQuery;
      
      if (error) {
        console.error("Error fetching recent rides:", error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        console.log("No recent rides found");
        return [];
      }
      
      console.log("Raw recent rides data:", data.length, "rides found");
      
      // Transform to BikeRide interface with bike name included
      const rides: BikeRide[] = data.map((ride: any): BikeRide => {
        const startTime = ride.actual_start_time ? new Date(ride.actual_start_time) : new Date(ride.created_at);
        const endTime = ride.actual_end_time ? new Date(ride.actual_end_time) : new Date();
        const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
        
        const bikeId = ride.bookings?.bike_id || 'unknown';
        const serialNumber = ride.bookings?.bikes?.serial_number || 'unknown';
        const modelId = ride.bookings?.bikes?.model_id || null;
        const bikeName = ride.bookings?.bikes?.model?.name || null;
        
        const startLocation = ride.bookings?.start_location_type === 'site'
          ? ride.bookings?.start_site?.name || 'Unknown Site'
          : 'Home';
        const endLocation = ride.bookings?.end_location_type === 'site'
          ? ride.bookings?.end_site?.name || 'Unknown Site'
          : 'Home';
        
        return {
          id: ride.id,
          bikeId: bikeId,
          serialNumber: serialNumber,
          modelId: modelId,
          bikeName: bikeName,
          userId: ride.user_id,
          userName: ride.users?.username || 'Unknown User',
          startTime,
          endTime,
          startLocation,
          endLocation,
          distance: ride.distance_traveled_km || 0,
          duration: durationMinutes,
          countTowardsOffset: ride.count_towards_offset !== false // Default to true if not specified
        };
      });
      
      console.log("Transformed recent rides:", rides.length);
      return rides;
    },
    refetchOnWindowFocus: false
  });

  const handleRefresh = () => {
    bikeQuery.refetch();
    userStatsQuery.refetch();
    recentRidesQuery.refetch();
  };

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    if (!recentRidesQuery.data || !bikeQuery.data || !userStatsQuery.data) {
      toast.error("No data available to export");
      return;
    }

    const exportData = {
      overview: userStatsQuery.data.overviewStats,
      bikes: bikeQuery.data.map(bike => ({
        model: bike.model?.name || 'Unknown',
        serialNumber: bike.serial_number,
        status: bike.status,
        batteryLevel: bike.battery_level || 0,
        location: bike.site?.name || 'Unassigned',
        distanceTraveled: bike.distance_traveled_km || 0
      })),
      users: [{
        name: userStatsQuery.data.userData.mostActiveUser?.name || 'Unknown',
        totalRides: userStatsQuery.data.userData.mostActiveUser?.totalRides || 0,
        totalDistance: userStatsQuery.data.userData.mostActiveUser?.totalDistance || 0,
        offsetDistance: userStatsQuery.data.overviewStats.totalOffsetDistance,
        nonOffsetDistance: userStatsQuery.data.overviewStats.totalDistance - userStatsQuery.data.overviewStats.totalOffsetDistance,
        co2Offset: userStatsQuery.data.overviewStats.totalCO2Offset
      }],
      rides: recentRidesQuery.data
    };

    exportStatisticsData(exportData, format);
  };

  // Refetch when active period changes
  useEffect(() => {
    userStatsQuery.refetch();
  }, [activePeriod]);

  return (
    <div className="container py-6 space-y-6">
      <StatisticsHeader 
        onRefresh={handleRefresh} 
        onExport={handleExport} 
      />
      
      {/* Combined Filter Row */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
        <DashboardFiltersComponent 
          filters={filters}
          onFiltersChange={setFilters}
        />
        <TimeRangeSelector 
          value={activePeriod} 
          onValueChange={setActivePeriod} 
        />
      </div>
      
      <Tabs defaultValue="overview" className="space-y-6">
        <StatisticsTabs />
        
        <OverviewTab 
          isLoading={userStatsQuery.isPending}
          activePeriod={activePeriod}
          monthlyData={userStatsQuery.data?.monthlyData || []}
          co2ByTypeData={userStatsQuery.data?.co2ByTypeData || []}
          overviewStats={userStatsQuery.data?.overviewStats}
        />
        
        <BikesTab 
          isLoading={bikeQuery.isPending}
          bikes={bikeQuery.data || []}
        />
        
        <UsersTab 
          isLoading={userStatsQuery.isPending}
          userStatisticsData={userStatsQuery.data?.userData || {}}
          userGrowthData={userStatsQuery.data?.userGrowthData || []}
        />
        
        <RidesTab 
          isLoading={recentRidesQuery.isPending}
          rides={recentRidesQuery.data || []}
        />
      </Tabs>
    </div>
  );
};

export default StatisticsPage;
